import React, { useState } from 'react';
import { Check, Users } from 'lucide-react';
import type { Brief, Role } from './types';

interface BriefSummaryProps {
  brief: Brief | null;
  roles: Role[];
  // Direct fill for a missing field (progressive checklist). Chat remains the
  // primary path; when absent the panel is display-only.
  onSetField?: (key: string, value: string) => Promise<void> | void;
}

const submissionLabel = (mode?: string) =>
  mode === 'full_tape' ? 'Photos, then self-tapes' : mode === 'photo' ? 'Photo submissions' : undefined;

// The required checklist, mirroring the server's completion gate.
const REQUIRED: { key: keyof Brief; label: string; hint: string }[] = [
  { key: 'title', label: 'Title', hint: 'What the project is called' },
  { key: 'production_type', label: 'Production', hint: 'Commercial, short film, social…' },
  { key: 'description', label: 'Description', hint: 'A line or two about the project' },
  { key: 'geography', label: 'Location', hint: 'Where it shoots' },
  { key: 'dates', label: 'Shoot dates', hint: 'A specific date, or a date range' },
  { key: 'rate', label: 'Rate', hint: 'What you’re paying' },
  { key: 'usage', label: 'Usage', hint: 'Where it runs, and for how long' },
  { key: 'submission_deadline', label: 'Submission deadline', hint: 'When submissions / self-tapes are due' },
  { key: 'submission_mode', label: 'Submissions', hint: 'Photos only, or photos then tapes' },
];

// Once this many required fields are captured, reveal the full checklist —
// early on, the sparse panel keeps the user talking to Katia instead of
// form-filling; late, seeing what's left gets them to the finish line.
const REVEAL_AT = 4;

// Beige-surface field: muted label, full-contrast value.
const Field: React.FC<{ label: string; value?: string }> = ({ label, value }) => {
  if (!value) return null;
  return (
    <div style={{ marginBottom: 10 }}>
      <div className="mono-label" style={{ color: 'var(--hj-beige-ink-3)', marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 14, color: 'var(--hj-beige-ink)', lineHeight: 1.45 }}>{value}</div>
    </div>
  );
};

// A missing required field: click to fill inline, or just keep chatting.
const MissingField: React.FC<{
  label: string;
  hint: string;
  fieldKey: string;
  onSetField?: (key: string, value: string) => Promise<void> | void;
}> = ({ label, hint, fieldKey, onSetField }) => {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    const v = value.trim();
    if (!v || !onSetField) return;
    setSaving(true);
    try {
      await onSetField(fieldKey, v);
      setEditing(false);
      setValue('');
    } finally {
      setSaving(false);
    }
  };

  // Submission mode is a two-choice pick, not free text.
  if (editing && fieldKey === 'submission_mode') {
    return (
      <div style={{ marginBottom: 10 }}>
        <div className="mono-label" style={{ color: 'var(--hj-beige-ink-3)', marginBottom: 4 }}>{label}</div>
        <div className="flex gap-2">
          {[{ v: 'photo', l: 'Photos only' }, { v: 'full_tape', l: 'Photos, then tapes' }].map(opt => (
            <button
              key={opt.v}
              type="button"
              disabled={saving}
              onClick={async () => { if (onSetField) { setSaving(true); try { await onSetField(fieldKey, opt.v); } finally { setSaving(false); } } }}
              style={{ padding: '7px 12px', borderRadius: 999, border: '1px solid var(--hj-beige-line)', background: '#fff', color: 'var(--hj-beige-ink)', fontSize: 12.5, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}
            >
              {opt.l}
            </button>
          ))}
        </div>
      </div>
    );
  }

  if (editing) {
    return (
      <div style={{ marginBottom: 10 }}>
        <div className="mono-label" style={{ color: 'var(--hj-beige-ink-3)', marginBottom: 4 }}>{label}</div>
        <input
          autoFocus
          value={value}
          onChange={e => setValue(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') save(); if (e.key === 'Escape') setEditing(false); }}
          onBlur={() => { if (!value.trim()) setEditing(false); }}
          placeholder={hint}
          disabled={saving}
          style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid var(--hj-beige-line)', background: '#fff', color: 'var(--hj-beige-ink)', fontSize: 13.5, fontFamily: 'inherit', outline: 'none' }}
        />
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => onSetField && setEditing(true)}
      className="w-full text-left"
      style={{ marginBottom: 10, padding: 0, background: 'none', border: 'none', cursor: onSetField ? 'pointer' : 'default', fontFamily: 'inherit' }}
    >
      <div className="mono-label" style={{ color: 'var(--hj-beige-ink-3)', marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 13.5, color: 'var(--hj-beige-ink-2)', lineHeight: 1.45, fontStyle: 'italic' }}>
        {hint}{onSetField ? ' — tell Katia, or tap to type it' : ''}
      </div>
    </button>
  );
};

// Live summary card — fills in as Katia captures the brief. Early on it only
// shows what's captured (chat is the induction); once REVEAL_AT fields are in,
// the full required checklist appears so the finish line is visible.
export const BriefSummary: React.FC<BriefSummaryProps> = ({ brief, roles, onSetField }) => {
  const b = brief || ({} as Brief);
  const filledCount = REQUIRED.filter(f => !!b[f.key]).length;
  const hasRole = roles && roles.length > 0 && !!roles[0].title;
  const totalDone = filledCount + (hasRole ? 1 : 0);
  const revealed = filledCount >= REVEAL_AT;
  const empty = filledCount === 0 && !hasRole;

  return (
    <div
      style={{
        borderRadius: 'var(--hj-r-md)',
        background: 'var(--hj-beige)',
        border: '1px solid var(--hj-beige-line)',
        color: 'var(--hj-beige-ink)',
        padding: 18,
      }}
    >
      <div className="flex items-center justify-between" style={{ marginBottom: 10 }}>
        <div className="mono-label" style={{ color: 'var(--hj-beige-ink-3)' }}>Your brief</div>
        {revealed && (
          <div className="mono-label inline-flex items-center gap-1" style={{ color: totalDone === REQUIRED.length + 1 ? 'var(--hj-beige-ink)' : 'var(--hj-beige-ink-3)' }}>
            {totalDone === REQUIRED.length + 1 && <Check className="w-3 h-3" />}
            {totalDone}/{REQUIRED.length + 1}
          </div>
        )}
      </div>

      {empty && (
        <p className="m-0" style={{ fontSize: 13.5, color: 'var(--hj-beige-ink-2)', lineHeight: 1.5 }}>
          As you answer Katia, your project details fill in here.
        </p>
      )}

      {!empty && (
        <>
          {b.title ? (
            <h3
              className="font-hj-display m-0"
              style={{ fontSize: 20, color: 'var(--hj-beige-ink)', letterSpacing: '-0.02em', fontWeight: 700, marginBottom: 12, lineHeight: 1.2 }}
            >
              {b.title}
            </h3>
          ) : revealed ? (
            <MissingField label="Title" hint="What the project is called" fieldKey="title" onSetField={onSetField} />
          ) : null}
          {REQUIRED.filter(f => f.key !== 'title').map(f =>
            b[f.key] ? (
              <Field key={f.key} label={f.label} value={f.key === 'submission_mode' ? submissionLabel(b.submission_mode) : String(b[f.key])} />
            ) : revealed ? (
              <MissingField key={f.key} label={f.label} hint={f.hint} fieldKey={f.key} onSetField={onSetField} />
            ) : null
          )}
          {b.union_status && <Field label="Union" value={b.union_status} />}
        </>
      )}

      {(hasRole || revealed) && !empty && (
        <div style={{ marginTop: 6, paddingTop: 14, borderTop: '1px solid var(--hj-beige-line)' }}>
          <div
            className="mono-label inline-flex items-center gap-1.5"
            style={{ color: 'var(--hj-beige-ink-3)', marginBottom: 10 }}
          >
            <Users className="w-3 h-3" /> Roles
          </div>
          {hasRole ? (
            <div className="flex flex-col gap-2">
              {roles.map((r, i) => (
                <div key={i}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--hj-beige-ink)', lineHeight: 1.3 }}>
                    {r.title}
                  </div>
                  {r.one_liner && (
                    <div style={{ fontSize: 12.5, color: 'var(--hj-beige-ink-2)', lineHeight: 1.4 }}>
                      {r.one_liner}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="m-0" style={{ fontSize: 13.5, color: 'var(--hj-beige-ink-2)', fontStyle: 'italic', lineHeight: 1.45 }}>
              Who you’re casting — tell Katia about each role
            </p>
          )}
        </div>
      )}
    </div>
  );
};
